import {
  Card,
  DiffViewerContentCols,
  DiffViewerTitleCols,
  Row,
} from '@/components';
import {
  useExpenditureBudgetPurpose,
  useMinistries,
} from '@/features/master-table/hooks';
import { usePolicies } from '@/features/master-table/policy/_hooks/policy';
import { getExpenditureBudgetPurposeConcatData } from '@/features/mold/utils';
import { diffRenderValueFormatter } from '@/features/project/_utils/diff-render-value-formatter';
import { useOrganizations } from '@/features/tenant-manager/organization/_hooks/organization';
import { useFilteredUsers } from '@/features/tenant-manager/user/_hooks/user';
import type { DiffViewConfigType } from '@/types/diff-view-config';
import {
  ITEM_SPLITTER,
  NEST_ITEM_CONNECTOR,
  findUpperRecursive,
} from '@/utils';
import {
  ProjectCategoryLabelModifier,
  SheetTypeLabelModifier,
} from '@/utils/enum';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  type ReviewSheetModel,
  SkipAdministrativeReformTeamEvaluationReasonLabelModifier,
} from '../../../_models/project';

type Project = z.infer<typeof ReviewSheetModel>;

type Props = {
  sourceProject: Project; // left side
  targetProject: Project; // right side
};

type DiffViewConfigItem = DiffViewConfigType<Project>;

export const RsSkippedReviewProjectDiffView: FC<Props> = ({
  sourceProject,
  targetProject,
}) => {
  const { t } = useTranslation();
  const organizations = useOrganizations();
  const policies = usePolicies();
  const users = useFilteredUsers({
    params: {
      filterBy: 'ids',
      ids: [
        ...new Set([
          ...(sourceProject.peopleInChargeIds ?? []),
          ...sourceProject.businessManagerIds,
          ...(targetProject.peopleInChargeIds ?? []),
          ...targetProject.businessManagerIds,
        ]),
      ],
      showDeleted: true,
    },
  });
  const ministries = useMinistries();

  const { data: sourceExpenditureBudgetPurposeData } =
    useExpenditureBudgetPurpose({
      id: sourceProject.majorExpenditureBudgetPurposeId || '',
    });
  const { data: targetExpenditureBudgetPurposeData } =
    useExpenditureBudgetPurpose({
      id: targetProject.majorExpenditureBudgetPurposeId || '',
    });

  const getMajorExpenditureBudgetPurposeNameById = (id: string) => {
    return {
      [sourceProject.majorExpenditureBudgetPurposeId || '']:
        sourceExpenditureBudgetPurposeData &&
        getExpenditureBudgetPurposeConcatData({
          data: sourceExpenditureBudgetPurposeData,
        }).name,
      [targetProject.majorExpenditureBudgetPurposeId || '']:
        targetExpenditureBudgetPurposeData &&
        getExpenditureBudgetPurposeConcatData({
          data: targetExpenditureBudgetPurposeData,
        }).name,
    }[id];
  };

  const diffViewConfig: DiffViewConfigItem[] = [
    {
      prop: 'sheetType',
      label: t('sheet-type'),
      converter: (value: Project['sheetType']) =>
        [
          SheetTypeLabelModifier[value],
          t('skip-administrative-reform-team-evaluation'),
        ].join(ITEM_SPLITTER),
    },
    {
      prop: 'ministryId',
      label: t('ministry'),
      converter: (value: Project['ministryId']) =>
        diffRenderValueFormatter.ministryId(value, ministries.data),
    },
    {
      prop: 'organizationId',
      label: t('organization-in-charge'),
      converter: (value: Project['organizationId']) =>
        findUpperRecursive(value, organizations.data ?? [])
          .reverse()
          .map((d) => d.name)
          .join(NEST_ITEM_CONNECTOR),
    },
    {
      prop: 'peopleInChargeIds',
      label: t('people-in-charge'),
      converter: (value: Project['peopleInChargeIds']) =>
        users.data?.results
          .flatMap((user) => (value?.includes(user.id) ? [user.name] : []))
          .join('\n') ?? '',
    },
    {
      prop: 'businessManagerIds',
      label: t('business-manager'),
      converter: (value: Project['businessManagerIds']) =>
        users.data?.results
          .flatMap((user) => (value.includes(user.id) ? [user.name] : []))
          .join('\n') ?? '',
    },
    {
      prop: 'otherResponsibleOrganizationIds',
      label: t('other-organization-in-charge'),
      converter: (values: Project['otherResponsibleOrganizationIds']) =>
        values
          .map((value) =>
            findUpperRecursive(value, organizations.data ?? [])
              .reverse()
              .map((d) => d.name)
              .join(NEST_ITEM_CONNECTOR),
          )
          .join('\n'),
    },
    {
      prop: 'relatedOrganizationIds',
      label: t('related-organizations'),
      converter: (values: Project['relatedOrganizationIds']) =>
        values
          .map((value) =>
            findUpperRecursive(value, organizations.data ?? [])
              .reverse()
              .map((d) => d.name)
              .join(NEST_ITEM_CONNECTOR),
          )
          .join('\n'),
    },
    {
      prop: 'skipAdministrativeReformTeamEvaluationReason',
      label: t('skip-administrative-reform-team-evaluation-reason'),
      converter: (
        value: Project['skipAdministrativeReformTeamEvaluationReason'],
      ) =>
        value
          ? SkipAdministrativeReformTeamEvaluationReasonLabelModifier[value]
          : '',
    },
    {
      prop: 'majorExpenditureBudgetPurposeId',
      label: t('expenditure-purpose-level-3-1'),
      converter: (value: Project['majorExpenditureBudgetPurposeId']) =>
        value ? getMajorExpenditureBudgetPurposeNameById(value) || '' : '',
    },
    {
      prop: 'category',
      label: t('project-category'),
      converter: (value: Project['category']) =>
        value ? ProjectCategoryLabelModifier[value] : '',
    },
    {
      prop: 'startYear',
      label: t('start-fiscal-year-of-project'),
      converter: (_, project) =>
        project?.startYear?.toString() ??
        (project?.isUnknownStart ? t('unknown-start') : t('blank')),
    },
    {
      prop: 'endYear',
      label: t('end-fiscal-year-of-project'),
      converter: (_, project) =>
        project?.endYear?.toString() ??
        (project?.isNoScheduledEnd ? t('no-scheduled-end') : t('blank')),
    },
    {
      prop: 'policiesForProject',
      label: t('policy'),
      converter: (value: Project['policiesForProject']) =>
        value
          .map((v) => {
            const policy = findUpperRecursive(v.policyId, policies.data ?? [])
              .reverse()
              .map((d) => d.name)
              .join(NEST_ITEM_CONNECTOR);
            return `${policy}${ITEM_SPLITTER}${v.policyUrl ?? ''}`;
          })
          .join('\n'),
      isLongField: true,
    },
  ];

  return (
    <Card>
      <Row gutter={[8, 0]}>
        <DiffViewerTitleCols label={t('project')} />
        {diffViewConfig.map(({ prop, label, converter, isLongField }) => (
          <DiffViewerContentCols<Project>
            key={`project-${prop}`}
            legend={label}
            prop={prop}
            textConverter={converter}
            isLongField={isLongField}
            source={sourceProject}
            target={targetProject}
          />
        ))}
      </Row>
    </Card>
  );
};
